import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "../api";
import { useAuth } from "../AuthContext";
import { t, LOCALES } from "../i18n";
import TaskCard from "../components/TaskCard";
import TaskDetailDialog from "../components/TaskDetailDialog";
import NewTaskDialog from "../components/NewTaskDialog";
import DashboardWidgets from "../components/DashboardWidgets";
import TimeClockHero from "../components/TimeClockHero";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Plus, ListChecks, Clock, Activity, CheckCircle2, Hash, ArrowRight, Inbox, Bookmark, CheckSquare, Square, Building2, Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const COLUMNS = [
    { key: "created", accent: "bg-slate-100 text-slate-700 border-slate-200" },
    { key: "taken", accent: "bg-blue-100 text-blue-800 border-blue-200" },
    { key: "working", accent: "bg-amber-100 text-amber-800 border-amber-200" },
    { key: "completed", accent: "bg-emerald-100 text-emerald-800 border-emerald-200" },
];

export default function DashboardPage() {
    const { user, lang } = useAuth();
    const [stats, setStats] = useState({ created: 0, taken: 0, working: 0, completed: 0, total: 0 });
    const [tasks, setTasks] = useState([]);
    const [myTasks, setMyTasks] = useState([]);
    const [myTodos, setMyTodos] = useState([]);
    const [users, setUsers] = useState([]);
    const [spaces, setSpaces] = useState([]);
    const [openTaskId, setOpenTaskId] = useState(null);
    const [taskSearch, setTaskSearch] = useState("");
    const [showHistory, setShowHistory] = useState(false);

    // Tasks completed in the last 24h are kept visible in the Erledigt column;
    // older completed tasks roll into the "Verlauf" archive that the user can expand.
    const COMPLETED_VISIBLE_MS = 24 * 60 * 60 * 1000;
    const isRecentlyCompleted = useCallback((tk) => {
        if (tk.status !== "completed") return true;
        if (!tk.completed_at) return true;       // legacy task — show until next move
        return (Date.now() - new Date(tk.completed_at).getTime()) <= COMPLETED_VISIBLE_MS;
    }, [COMPLETED_VISIBLE_MS]);

    // Filter Kanban tasks by title, description, space name, or assignee name
    const filteredTasks = useMemo(() => {
        const q = taskSearch.trim().toLowerCase();
        if (!q) return tasks;
        return tasks.filter((tk) => {
            const sp = spaces.find((s) => s.id === tk.space_id);
            const assignees = (tk.assignee_ids || [])
                .map((id) => users.find((u) => u.id === id)?.name || "")
                .join(" ");
            const haystack = [
                tk.title, tk.description, sp?.name, assignees,
                tk.priority, tk.status,
            ].filter(Boolean).join(" ").toLowerCase();
            return haystack.includes(q);
        });
    }, [tasks, taskSearch, spaces, users]);
    const [newTaskOpen, setNewTaskOpen] = useState(false);

    const loadAll = useCallback(async () => {
        try {
            const [s, t1, t2, u, sp, td] = await Promise.all([
                api.get("/stats"),
                api.get("/tasks"),
                api.get("/tasks/my"),
                api.get("/users"),
                api.get("/spaces"),
                api.get("/todos"),
            ]);
            setStats(s.data);
            setTasks(t1.data);
            setMyTasks(t2.data);
            setUsers(u.data);
            setSpaces(sp.data);
            setMyTodos(td.data || []);
        } catch (e) {}
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    // Refresh when a task changes from the global (notification-opened) dialog
    useEffect(() => {
        const h = () => loadAll();
        window.addEventListener("taskflow:task-changed", h);
        window.addEventListener("mytacs:tick", h);
        return () => {
            window.removeEventListener("taskflow:task-changed", h);
            window.removeEventListener("mytacs:tick", h);
        };
    }, [loadAll]);

    const handleDragOver = (e) => e.preventDefault();
    const handleDrop = async (e, status) => {
        e.preventDefault();
        const taskId = e.dataTransfer.getData("task-id");
        if (!taskId) return;
        try {
            await api.patch(`/tasks/${taskId}`, { status });
            loadAll();
        } catch {}
    };

    return (
        <div className="p-4 sm:p-6 md:p-8 space-y-6 md:space-y-8" data-testid="dashboard-page">
            {/* Header */}
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mb-1">{t(lang, "dashboard")}</p>
                    <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-neutral-900">
                        {lang === "tr" ? "Genel Görünüm" : lang === "de" ? "Übersicht" : "Overview"}
                    </h1>
                </div>
                <Button onClick={() => setNewTaskOpen(true)} className="bg-neutral-900 hover:bg-neutral-800 h-10" data-testid="dashboard-new-task-btn">
                    <Plus className="w-4 h-4 mr-1" /> {t(lang, "newTask")}
                </Button>
            </div>

            {/* Compact module widgets */}
            <TimeClockHero lang={lang} />
            <DashboardWidgets
                lang={lang}
                myTasksOpenCount={(myTasks || []).filter((tk) => tk.status !== "completed").length}
                myTodosOpenCount={(myTodos || []).filter((td) => !td.completed).length}
            />

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="stats-row">
                <StatCard label={t(lang, "total")} value={stats.total} icon={Hash} highlight />
                <StatCard label={t(lang, "created")} value={stats.created} icon={ListChecks} />
                <StatCard label={t(lang, "taken")} value={stats.taken} icon={Clock} />
                <StatCard label={t(lang, "working")} value={stats.working} icon={Activity} />
                <StatCard label={t(lang, "completed")} value={stats.completed} icon={CheckCircle2} />
            </div>

            {/* Main grid: kanban + my-tasks panel */}
            <div className="grid grid-cols-1 xl:grid-cols-[1fr,320px] gap-6">
                {/* Kanban */}
                <div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                        <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight">
                            {lang === "tr" ? "Görev Akışı" : lang === "de" ? "Aufgabenfluss" : "Task Flow"}
                            {taskSearch && (
                                <span className="ml-2 text-xs font-normal text-neutral-500">
                                    {filteredTasks.length} / {tasks.length}
                                </span>
                            )}
                        </h2>
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400 pointer-events-none" />
                            <input
                                type="text"
                                value={taskSearch}
                                onChange={(e) => setTaskSearch(e.target.value)}
                                placeholder={lang === "tr" ? "Görev ara…" : lang === "de" ? "Aufgaben durchsuchen…" : "Search tasks…"}
                                className="w-full h-9 pl-8 pr-8 text-sm border border-neutral-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition"
                                data-testid="kanban-search"
                            />
                            {taskSearch && (
                                <button
                                    type="button"
                                    onClick={() => setTaskSearch("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700"
                                    data-testid="kanban-search-clear"
                                    aria-label="leeren"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kanban-board">
                        {COLUMNS.map((col) => {
                            const allColTasks = filteredTasks.filter((tk) => tk.status === col.key);
                            const isCompletedCol = col.key === "completed";
                            const recentColTasks = isCompletedCol ? allColTasks.filter(isRecentlyCompleted) : allColTasks;
                            const archivedColTasks = isCompletedCol ? allColTasks.filter((tk) => !isRecentlyCompleted(tk)) : [];
                            const colTasks = isCompletedCol && !showHistory ? recentColTasks : allColTasks;
                            return (
                                <div
                                    key={col.key}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, col.key)}
                                    data-testid={`kanban-column-${col.key}`}
                                    className="flex flex-col gap-2.5 rounded-lg border border-neutral-200 bg-neutral-50/70 p-3 min-h-[280px] lg:min-h-[400px]"
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-2">
                                            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md border ${col.accent}`}>
                                                {t(lang, col.key)}
                                            </span>
                                            <span className="text-xs text-neutral-500">
                                                {colTasks.length}
                                                {isCompletedCol && !showHistory && archivedColTasks.length > 0 && (
                                                    <span className="text-neutral-400"> +{archivedColTasks.length}</span>
                                                )}
                                            </span>
                                        </div>
                                        {isCompletedCol && (recentColTasks.length > 0 || archivedColTasks.length > 0) && (
                                            <button
                                                onClick={() => setShowHistory((v) => !v)}
                                                type="button"
                                                className="text-[10px] font-medium text-brand-700 hover:text-brand-900 px-1.5 py-0.5 rounded hover:bg-brand-50 transition"
                                                data-testid="kanban-history-toggle"
                                                title={lang === "de" ? "Älter als 24h ein-/ausblenden" : "Toggle older items"}
                                            >
                                                {showHistory
                                                    ? (lang === "de" ? "Verlauf ausblenden" : "Hide history")
                                                    : (lang === "de" ? `Verlauf (${archivedColTasks.length})` : `History (${archivedColTasks.length})`)}
                                            </button>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        {colTasks.length === 0 && (
                                            <div className="flex flex-col items-center justify-center py-8 text-neutral-400">
                                                <Inbox className="w-5 h-5 mb-1" />
                                                <p className="text-xs">—</p>
                                            </div>
                                        )}
                                        {colTasks.map((tk) => (
                                            <TaskCard
                                                key={tk.id}
                                                task={tk}
                                                users={users}
                                                onOpen={() => setOpenTaskId(tk.id)}
                                                lang={lang}
                                                draggable
                                                onDragStart={(e) => e.dataTransfer.setData("task-id", tk.id)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* My Tasks panel - desktop sidebar, mobile below */}
                <aside className="xl:sticky xl:top-20 xl:h-fit space-y-4" data-testid="my-tasks-panel">
                    <MyTasksSection myTasks={myTasks} lang={lang} spaces={spaces} setOpenTaskId={setOpenTaskId} loadAll={loadAll} />
                </aside>
            </div>

            <NewTaskDialog
                open={newTaskOpen}
                onClose={() => setNewTaskOpen(false)}
                onCreated={loadAll}
                spaces={spaces}
                users={users}
            />
            <TaskDetailDialog
                taskId={openTaskId}
                open={!!openTaskId}
                onClose={() => setOpenTaskId(null)}
                onChange={loadAll}
                allUsers={users}
                spaces={spaces}
                onOpenTask={(id) => setOpenTaskId(id)}
            />
        </div>
    );
}

function StatCard({ label, value, icon: Icon, highlight }) {
    return (
        <div
            data-testid={`stat-${label}`}
            className={`rounded-lg border p-3 sm:p-4 transition ${
                highlight
                    ? "border-neutral-900 bg-neutral-900 text-white"
                    : "border-neutral-200 bg-white"
            }`}
        >
            <div className="flex items-center justify-between mb-2">
                <span className={`text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.15em] ${highlight ? "text-neutral-300" : "text-neutral-500"} truncate`}>
                    {label}
                </span>
                <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${highlight ? "text-white" : "text-neutral-400"}`} />
            </div>
            <p className={`font-display text-2xl sm:text-3xl font-bold tracking-tight ${highlight ? "text-white" : "text-neutral-900"}`}>{value}</p>
        </div>
    );
}

function MyTasksSection({ myTasks, lang, spaces, setOpenTaskId, loadAll }) {
    const [showCompleted, setShowCompleted] = useState(false);
    const active = myTasks.filter((tk) => tk.status !== "completed");
    const done = myTasks.filter((tk) => tk.status === "completed");

    const renderTask = (tk) => {
        const overdue = tk.deadline && tk.status !== "completed" && new Date(tk.deadline) < new Date();
        return (
            <li key={tk.id}>
                <button
                    onClick={() => {
                        if (tk.is_approval) {
                            window.location.href = tk.approval_link;
                        } else if (tk.is_private_todo) {
                            api.patch(`/todos/${tk.id}`, { completed: tk.status !== "completed" }).then(loadAll);
                        } else {
                            setOpenTaskId(tk.id);
                        }
                    }}
                    data-testid={`my-task-${tk.id}`}
                    className={`w-full flex items-start gap-2.5 px-3 py-2.5 text-left transition ${
                        overdue ? "bg-red-50/70 hover:bg-red-100 border-l-4 border-red-500" :
                        tk.status === "completed" ? "bg-emerald-50/70 hover:bg-emerald-50" :
                        "hover:bg-neutral-50"
                    }`}
                >
                    <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                        overdue ? "bg-red-600" :
                        tk.status === "completed" ? "bg-emerald-500" :
                        tk.status === "working" ? "bg-amber-500" :
                        tk.status === "taken" ? "bg-blue-500" : "bg-neutral-300"
                    }`} />
                    <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate flex items-center gap-1.5 ${
                            overdue ? "text-red-900 font-semibold" :
                            tk.status === "completed" ? "line-through text-emerald-700/80" :
                            "text-neutral-900"
                        }`}>
                            {tk.is_private_todo && <span title="Privat">🔒</span>}
                            {tk.is_approval && <span className="text-amber-700 text-[10px] font-bold bg-amber-100 px-1 rounded uppercase tracking-wider">{lang === "de" ? "Freigabe" : "Approval"}</span>}
                            {tk.assigned_to_space && !tk.claimed_by && !tk.is_approval && (
                                <Building2 className="w-3 h-3 text-blue-600 shrink-0" title={t(lang, "departmentTask")} />
                            )}
                            <span className="truncate">{tk.title}</span>
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                            <p className={`text-[11px] truncate ${overdue ? "text-red-700 font-semibold" : tk.status === "completed" ? "text-emerald-700/70" : "text-neutral-500"}`}>
                                {tk.is_private_todo ? "🔒 Privat" : tk.is_approval ? "Genehmigung" : spaces.find((s) => s.id === tk.space_id)?.name || "—"}
                                {tk.parent_task_id && " · sub"}
                            </p>
                            {tk.deadline && (
                                <span className={`text-[10px] ${overdue ? "text-red-700 font-bold" : tk.status === "completed" ? "text-emerald-700/60" : "text-neutral-500"}`}>
                                    {overdue && "⚠ "}{new Date(tk.deadline).toLocaleDateString(LOCALES[lang])}
                                </span>
                            )}
                        </div>
                    </div>
                    <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${
                        overdue ? "bg-red-100 text-red-800 border-red-300" :
                        tk.status === "completed" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                        tk.priority === "high" ? "bg-red-50 text-red-700 border-red-200" :
                        tk.priority === "medium" ? "bg-orange-50 text-orange-700 border-orange-200" :
                        "bg-stone-50 text-stone-700 border-stone-200"
                    }`}>
                        {overdue ? "ÜBERFÄLLIG" : tk.status === "completed" ? "✓" : tk.priority}
                    </span>
                </button>
            </li>
        );
    };

    return (
        <>
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight flex items-center gap-2">
                        <Bookmark className="w-4 h-4" />
                        {t(lang, "myTasks")}
                    </h2>
                    <span className="text-xs text-neutral-500">{active.length} {t(lang, "tasks")}</span>
                </div>
                <div className="rounded-lg border border-neutral-200 bg-white" data-testid="my-tasks-list">
                    {active.length === 0 ? (
                        <div className="p-6 text-center text-neutral-500 text-sm">
                            <Inbox className="w-6 h-6 mx-auto mb-2 text-neutral-400" />
                            {t(lang, "noTasks")}
                        </div>
                    ) : (
                        <ScrollArea className="max-h-[600px]">
                            <ul className="divide-y divide-neutral-100">
                                {active.map(renderTask)}
                            </ul>
                        </ScrollArea>
                    )}
                </div>
            </div>

            {done.length > 0 && (
                <div data-testid="my-completed-section">
                    <button
                        onClick={() => setShowCompleted((v) => !v)}
                        className="w-full flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-neutral-500 hover:text-neutral-900 transition py-2"
                        data-testid="my-completed-toggle"
                    >
                        <span className="flex items-center gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            {lang === "de" ? "Erledigt" : "Completed"} · {done.length}
                        </span>
                        <span>{showCompleted ? "▾" : "▸"}</span>
                    </button>
                    {showCompleted && (
                        <div className="rounded-lg border border-neutral-200 bg-white" data-testid="my-completed-list">
                            <ScrollArea className="max-h-[400px]">
                                <ul className="divide-y divide-neutral-100">
                                    {done.map(renderTask)}
                                </ul>
                            </ScrollArea>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
